import { ReactElement, useEffect, useState } from 'react';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import {
  Alert,
  DashboardDataFilter,
  DeviceAttributesV1,
  DeviceType,
  ParametersType,
  ParticleData,
  PriorityList
} from '../constants/telosair-interfaces';
import { getPriorityColor } from '../_helpers/dashboardHelper';
import { classesBasedOnPriority } from '../_helpers/globalHelper';
import { useUnifiedData } from '../hooks/useUnifiedData';

type DeviceHoverState = {
  x: number;
  y: number;
  deviceData: DeviceAttributesV1;
  particleData: ParticleData;
  alert: Alert[];
};

type MapGroupMarkerProps = {
  deviceData: DeviceAttributesV1;
  selectedParameters: ParametersType[];
  selectedDevice?: number | null;
  setHovered: (hovered: DeviceHoverState | null) => void;
  shouldHighlight?: boolean;
  setDataFilter?: (filter: DashboardDataFilter | null) => void;
};

/**
 * A component to display a device marker on a Google Map.
 * It shows the device marker and provides click information.
 * @param props - Props for the component.
 * @returns A Google Map marker component with device information.
 */
export default function MapGroupMarker(props: MapGroupMarkerProps): ReactElement {
  const {
    deviceData,
    selectedParameters,
    selectedDevice,
    setHovered,
    shouldHighlight,
    setDataFilter
  } = props;
  const [priority, setPriority] = useState<PriorityList>(PriorityList.none);
  const [isClicked, setIsClicked] = useState<boolean>(false);

  const { clientThresholdSettings, deviceDataSummary, userConfigDataV2, allAlerts, devices } =
    useUnifiedData();
  const particleData = deviceDataSummary[deviceData.serial_number]?.parameters || {};

  useEffect(() => {
    const priorityList: PriorityList[] = [];
    Object.entries(particleData)
      .filter(([key]) =>
        selectedParameters.length ? selectedParameters?.includes(key as ParametersType) : true
      )
      .forEach(([key, value]) => {
        if (value !== null) {
          const deviceType =
            devices.find((item) => item.serialNumber === deviceData.serial_number)?.deviceType ||
            DeviceType.MIXED;
          const particlePriority = getPriorityColor(
            key as ParametersType,
            value,
            clientThresholdSettings,
            userConfigDataV2,
            deviceType
          );
          priorityList.push(particlePriority);
        }
      });
    if (priorityList.includes(PriorityList.high)) {
      setPriority(PriorityList.high);
      return;
    }
    if (priorityList.includes(PriorityList.medium)) {
      setPriority(PriorityList.medium);
      return;
    }
    if (priorityList.includes(PriorityList.low)) {
      setPriority(PriorityList.low);
    }
  }, [particleData, selectedParameters]);

  const alert = allAlerts.filter(
    (item) =>
      parseInt(item.serialNumber, 10) === deviceData.serial_number && item.status === 'pending'
  );

  /**
   * GetSize for Device Icon returns the size of the device (circle) Icon on the bases of the size.
   * @returns - Classes.
   */
  const getClassesForDeviceIcon = (): string => {
    if (shouldHighlight && priority === PriorityList.high) {
      return 'animate-ping hover:bg-opacity-90';
    }
    return '';
  };

  /**
   * Handles the click event for the marker.
   * @param e - The click event.
   */
  const handleClick = (e: any): void => {
    setIsClicked(!isClicked);
    setDataFilter?.({ type: 'device', id: deviceData.serial_number, name: deviceData.deviceName });

    const hoverCardHeight = 500;
    const windowHeight = window.innerHeight;
    let yPosition = e.domEvent.clientY;

    if (yPosition + hoverCardHeight > windowHeight) {
      yPosition = windowHeight - hoverCardHeight;
    }

    if (yPosition < 0) {
      yPosition = 0;
    }

    setHovered({
      y: yPosition,
      x: e.domEvent.clientX,
      deviceData,
      particleData,
      alert
    });
  };

  return (
    <AdvancedMarker
      key={deviceData.id}
      position={{ lat: deviceData.y, lng: deviceData.x }}
      title={deviceData.deviceName}
      onClick={handleClick}
    >
      <div className="flex flex-col items-center">
        <div
          className={`${getClassesForDeviceIcon()} ${classesBasedOnPriority(priority, true)} ${
            selectedDevice === deviceData.serial_number && 'border-2'
          } ${alert.length > 0 && 'pulse-scale'}  rounded-full material-icons-outlined scale-75`}
        >
          memory
        </div>
        <div className="text-base mt-1 px-2 py-1 shadow-lg font-bold bg-white inline-block whitespace-nowrap">
          {deviceData.deviceName}
        </div>
      </div>
    </AdvancedMarker>
  );
}
